import { SlSnackBarService } from 'sl-dev-components';
// src/app/core/store/auth.store.ts
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';

import { environment } from '../../../environments/environment';
import { JwtResponse } from '../models/jwt-response.model';
import { LoginRequest } from '../models/login-request.model';
import { UserDto } from '../models/dto/user.dto';

type Nullable<T> = T | null;

@Injectable({ providedIn: 'root' })
export class AuthStore {
    private http = inject(HttpClient);
    private router = inject(Router);
    private snackBar = inject(SlSnackBarService);

    // ----- STATE (signals)
    private _accessToken = signal<Nullable<string>>(null);
    private _user = signal<Nullable<UserDto>>(null);

    // ----- SELECTORS (computed)
    accessToken = computed(() => this._accessToken());
    user = computed(() => this._user());
    companyId = computed(() => this._user()?.company?.id ?? this._user()?.companyId ?? null);

    private tokenExp = computed<number | null>(() => {
        const t = this._accessToken();
        if (!t) return null;
        try {
            const payload = JSON.parse(atob(t.split('.')[1]));
            return typeof payload?.exp === 'number' ? payload.exp : null;
        } catch {
            return null;
        }
    });

    isTokenExpired = computed<boolean>(() => {
        const exp = this.tokenExp();
        if (!exp) return true;
        // exp viene en segundos
        const now = Math.floor(Date.now() / 1000);
        return now >= exp;
    });

    isAuthenticated = computed<boolean>(() => {
        const t = this._accessToken();
        return !!t && !this.isTokenExpired();
    });

    // Efecto: guarda en localStorage cuando cambie el token o el user
    private persistEffect = effect(() => {
        const token = this._accessToken();
        const user = this._user();
        if (token) localStorage.setItem('access_token', token);
        else localStorage.removeItem('access_token');

        if (user) localStorage.setItem('user', JSON.stringify(user));
        else localStorage.removeItem('user');
    });

    // ----- API URLs
    private base = environment.apiUrl.endsWith('/')
        ? environment.apiUrl
        : environment.apiUrl + '/';

    // ----- INIT (hydratar desde localStorage)
    hydrateFromStorageAndRefreshUser = async (): Promise<void> => {
        const token = localStorage.getItem('access_token');
        const userStr = localStorage.getItem('user');
        if (token) this._accessToken.set(token);
        if (userStr) {
            try { this._user.set(JSON.parse(userStr)); } catch { }
        }

        // Solo logout silencioso si no hay token, pero NO hacer refresh en init
        // para evitar bloquear la app
        if (!token || this.isTokenExpired()) {
            this.logout({ silent: true });
        }

        // El refresh de usuario se puede hacer después en background si es necesario
    };

    // ----- ACTIONS
    login = (credentials: LoginRequest) => {

        return this.http.post<JwtResponse>(this.base + 'auth/login', credentials)
            .subscribe({
                next: (res) => {

                    const token = res?.accessToken ?? (res as any)?.token; // por si mantienes alias "token"
                    if (!token || !res?.user) throw new Error('Respuesta de login inválida');

                    console.log('[AuthStore] Setting token and user...');
                    this._accessToken.set(token);
                    this._user.set(res.user);
                    console.log('[AuthStore] Token and user set, navigating to /analytics...');
                    // navegar a analytics (no carga datos automáticamente)
                    this.router.navigate(['/analytics']).then(() => {
                        console.log('[AuthStore] Navigation to /analytics completed');
                    }).catch(err => {
                        console.error('[AuthStore] Navigation failed:', err);
                    });
                },
                error: (err) => {
                    console.error('[AuthStore] Login error', err);
                    
                    // Mostrar mensaje de error al usuario
                    let errorMessage = 'Error del servidor. Intente más tarde';
                    
                    if (err.status === 401) {
                        errorMessage = err.error?.message || 'Usuario o contraseña incorrectos';
                    } else if (err.status === 403) {
                        errorMessage = err.error?.message || 'Usuario bloqueado o desactivado';
                    } else if (err.status === 0) {
                        errorMessage = 'No se puede conectar con el servidor';
                    }
                    
                    this.snackBar.open(errorMessage, 'Cerrar', { 
                        duration: 5000,
                        horizontalPosition: 'center',
                        verticalPosition: 'top',
                        panelClass: ['error-snackbar']
                    });
                    
                    throw err;
                }
            });
    }

    logout = (opts?: { silent?: boolean }) => {
        this._accessToken.set(null);
        this._user.set(null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        if (!opts?.silent) this.router.navigateByUrl('/login');
    };

    setSession = (token: string | null, user: UserDto | null) => {
        this._accessToken.set(token);
        this._user.set(user);
    };

    refreshMe = () => {
        const headers = new HttpHeaders({ 'Accept': 'application/json' });
        return this.http.get<UserDto>(this.base + 'auth/me', { headers })
            .pipe()
            .toPromise()
            .then((u) => {
                if (u) this._user.set(u);
                return u;
            });
    };

    // Disponible si en algún momento QUIERES renovar token manualmente
    refreshToken = () => {
        const headers = new HttpHeaders({ 'Accept': 'application/json' });
        return this.http.post<{ accessToken: string }>(this.base + 'auth/refresh', {}, { headers })
            .subscribe({
                next: (r) => {
                    if (r?.accessToken) this._accessToken.set(r.accessToken);
                },
                error: (err) => console.error('refreshToken error', err)
            });
    };
}
